// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.FilterDetailsConfig.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

/**
 * FilterDetailsConfig model
 */
import { observable, computed, action, transaction, toJS } from 'mobx';
import {
  placeholder,
  concat,
  includes,
  lowerCase,
  get,
  contains,
  isEqual,
  compact,
  isArray,
  find,
  isNil,
  indexOf,
  isObject,
  getOr,
  partial,
  flow,
  filter,
  negate,
  map,
  join,
  isFinite,
  isEmpty,
  flatMap,
  isUndefined
} from 'lodash/fp';
import {
  DEFAUFT_VALUE,
  OPERATOR_VALUE_SOURCE,
  OPERATOR_VALUE_LIST,
  OPERATER_BY_DATA_TYPE,
  DEFAUFT_OPERATOR_GROUP,
  OPERATOR_GROUP,
  BLANK_NULL_OPERATOR
} from 'logic/data/Izenda.FilterDetailsConfigConstants';
import { INSERT, UPDATE } from 'logic/data/Izenda.ObjectStateConstants';
import tracker from 'share/tracking/Izenda.Tracker';
import reportPartUtils from 'share/utils/Izenda.ReportPartUtils';
import uiState from 'logic/Izenda.UiState';
import { concatArray } from 'share/utils/Izenda.LazyLoadingUtils';

require('share/utils/Izenda.StringUtilities');

@tracker
export default class FilterDetailsConfig {
  markUpdateResult: () => void;

  @observable fieldDetails = {};

  @observable commonData = { operatorGroup: [] };

  @observable selectedOperatorId: string;

  @observable
  operatorSourceInfo = {
    sourceType: '',
    data: []
  };

  @observable
  operatorSourceDisplay = {
    isLastPage: false,
    data: []
  };

  @observable populatedData = false;
  @observable isValueChangeFromPopup = false;
  @computed
  get filterValue() {
    return this.parseFilterValue(this.fieldDetails.value, true);
  }

  @computed
  get operatorGroup() {
    const dataType = (this.fieldDetails && this.fieldDetails.dataType) || '';
    const def = concat(OPERATER_BY_DATA_TYPE[dataType.toLowerCase()], [OPERATOR_GROUP.UNKNOWN]);

    return this.commonData.operatorGroup.filter(({ key }) => def.indexOf(key) > -1);
  }

  /*
   * exclude COMPARISON/FIELDCOMPARISON group if filter is SP
   */
  @computed
  get availableOperatorGroup() {
    if (this.fieldDetails.isPredicated) {
      return this.operatorGroup.filter(({ key }) => {
        return OPERATOR_GROUP.FIELDCOMPARISON !== key;
      })
    }
    return this.fieldDetails.isParameter
      ? this.operatorGroup.filter(
        ({ key }) =>
          !includes(lowerCase(key), [
            OPERATOR_GROUP.COMPARISON,
            OPERATOR_GROUP.FIELDCOMPARISON,
            OPERATOR_GROUP.STRING
          ])
      )
      : this.operatorGroup;
  }

  @computed
  get operators() {
    return get('group.filterOperators', this.filterInfo);
  }

  @computed
  get operatorSourceValue() {
    return this.operatorSourceInfo.data;
  }

  @computed
  get isUpdateSource() {
    return this.filterInfo.sourceType !== this.operatorSourceInfo.sourceType;
  }

  @computed
  get filterInfo() {
    const generalInfo = this.getGroupAndOperatorByOperatorId(this.fieldDetails.operatorId) || {};
    const sGroup = generalInfo.group;
    const sOperator = generalInfo.operator;

    return {
      groupId: sGroup && sGroup.id,
      group: sGroup,
      operatorId: sOperator && sOperator.id,
      operator: sOperator,
      value: this.filterValue,
      sourceType: generalInfo.sourceTypeByGroup
    };
  }

  @computed
  get sourceFieldName() {
    return this.fieldDetails.sourceFieldName;
  }

  @computed
  get doesSupportDataSourceLazyLoading() {
    return contains(this.filterInfo.sourceType, ['dataSource', 'dataSourceTree']);
  }

  constructor(fieldDetails, markUpdateResult) {
    this.markUpdateResult = markUpdateResult;

    if (fieldDetails) {
      isUndefined(fieldDetails.markUpdateResult) && (fieldDetails.markUpdateResult = markUpdateResult);
      this.setFieldDetails(fieldDetails);
    }

    this.resetDirty();
  }

  /**
   * Check Dirty
   */
  checkDirty() {
    return this.isDirty;
  }

  /**
   * Clean Dirty
   */
  cleanDirty() {
    this.resetDirty();
  }

  /**
   * Update list of operator group (mostly this data comes from API)
   * @param {Array<any>} operatorGroups
   */
  populateOperatorGroup(operatorGroup: Array<any>) {
    this.commonData.operatorGroup = operatorGroup;
  }

  /**
   * Parse filter value from string to array
   * List of values wil be join by [;#]
   * @param {string|Array<any>} filterValue
   * @param {boolean} flag
   *              TRUE    parse from string to array (data from server)
   *              FALSE   parse from array to string (to save data)
   * @returns {string|Array<any>}
   */
  parseFilterValue(filterValue: any, flag: boolean) {
    if (flag) {
      filterValue = filterValue || '';
      return (filterValue && filterValue.split(';#')) || [];
    } else {
      filterValue = filterValue || [];
      return isEqual(compact(filterValue), []) ? '' : filterValue.join(';#');
    }
  }

  /**
   * Set filter value (filter value is depended on selected-operator-group and selected-operator)
   * @param {string|Array<any>} filterValue
   */
  setFilterValue(filterValue: any) {
    filterValue = isArray(filterValue) ? filterValue : [filterValue];
    filterValue = this.parseFilterValue(filterValue, false);
    this.fieldDetails && this.fieldDetails.updateByKey('value', filterValue);
  }

  /**
   * Indicate is value chanege of filter.
   * @param {string|Array<any>} filterValue
   */
  setIsValueChangeFromPopup(value: any) {
    this.isValueChangeFromPopup = value;
  }

  /**
   * Update filter value
   * @param {Object} subData
   * @param {string} filterFieldId
   * @param {boolean} isDashboardDetail
   * @param {string|Array<any>} filterValue
   * @param {string} operatorId
   * @param {string} operatorSetting
   */
  updateFilterValue(
    subData: any,
    filterFieldId: string,
    isDashboardDetail: boolean,
    filterValue: any,
    operatorId: string,
    operatorSetting: string
  ) {
    if (isDashboardDetail) {
      return this.updateCommonFilterValue(subData, filterFieldId, filterValue, operatorId, operatorSetting);
    }

    filterValue = isArray(filterValue) ? filterValue : [filterValue];
    filterValue = this.parseFilterValue(filterValue, false);

    this.fieldDetails && this.fieldDetails.updateByKey('value', filterValue);

    const filter = find(s => isEqual(s.filterFieldId, filterFieldId), subData.subscriptionFilterFields);

    if (isNil(filter)) {
      subData.subscriptionFilterFields.push({
        filterFieldId,
        value: filterValue,
        state: INSERT,
        operatorId: operatorId,
        operatorSetting: operatorSetting
      });
    } else {
      const i = indexOf(filter, subData.subscriptionFilterFields);
      subData.subscriptionFilterFields[i].operatorId = operatorId;
      subData.subscriptionFilterFields[i].operatorSetting = operatorSetting;
      subData.subscriptionFilterFields[i].value = filterValue;
      subData.subscriptionFilterFields[i].state !== INSERT && (subData.subscriptionFilterFields[i].state = UPDATE);
    }
    this.markDirty();
    this.markUpdateResult && this.markUpdateResult();
  }

  /**
   * Update CommonFilterValue
   * @param {string} filterFieldId
   * @param {string|Array<any>} filterValue
   * @param {string} operatorId
   * @param {string} operatorSetting
   */
  updateCommonFilterValue(
    { subscriptionCommonFilterFields },
    filterFieldId: string,
    filterValue: any,
    operatorId: string,
    operatorSetting: string
  ) {
    filterValue = isArray(filterValue) ? filterValue : [filterValue];
    filterValue = this.parseFilterValue(filterValue, false);

    this.fieldDetails && this.fieldDetails.updateByKey('value', filterValue);

    const filter = find(
      ({ commonFilterFieldId }) => isEqual(commonFilterFieldId, filterFieldId),
      subscriptionCommonFilterFields
    );

    if (isNil(filter)) {
      subscriptionCommonFilterFields.push({
        commonFilterFieldId: filterFieldId,
        value: filterValue,
        state: INSERT,
        operatorId: operatorId,
        operatorSetting: operatorSetting
      });
    } else {
      const i = indexOf(filter, subscriptionCommonFilterFields);
      subscriptionCommonFilterFields[i].value = filterValue;
      subscriptionCommonFilterFields[i].state !== INSERT && (subscriptionCommonFilterFields[i].state = UPDATE);
    }
    this.markDirty();
    this.markUpdateResult && this.markUpdateResult();
  }

  /**
   * Update operatorId and filter value from filterInfo to field-details
   */
  @action
  updateFilterValueToField() {
    const filterInfo = this.filterInfo;

    this.setOperatorId(filterInfo.operatorId, false);
    this.setFilterValue(filterInfo.value);
  }

  /**
   * Get operator group's id by operator id
   * @param {string} operatorId
   */
  getGroupByOperatorId(operatorId: string) {
    const operator = this.getOperatorById(operatorId);

    return operator && this.getGroupById(operator.operatorGroupId);
  }

  /**
   * Return details of operator group based on group Id
   * @param {string} operatorGroupId
   * @returns {Object}
   */
  getGroupById(operatorGroupId: string) {
    return find({ id: operatorGroupId }, this.commonData.operatorGroup);
  }

  /**
   * Return details of operator group based on group key
   * @param {string} groupKey
   * @returns {Object}
   */
  getGroupByKey(groupKey: string) {
    return find({ key: groupKey }, this.commonData.operatorGroup);
  }

  /**
   * Get details of operator based on operator Id
   * @param {string} operatorId
   * @returns {Object}
   */
  getOperatorById(operatorId: string) {
    return flow(
      flatMap(({ filterOperators }) => toJS(filterOperators)),
      find({ id: operatorId })
    )(this.commonData.operatorGroup);
  }

  /**
   * Return operator details of certain group based on index
   * @param {string} operatorGroupId
   * @param {string} dataType
   * @returns {Object}
   */
  getOperatorByIndex(operatorGroupId: string, dataType: string) {
    const group = this.getGroupById(operatorGroupId) || {};
    const operators = group.filterOperators || [];
    const isBlankOperator = BLANK_NULL_OPERATOR.indexOf(dataType) > -1;

    return (!isBlankOperator && find({ id: group.defaultOperatorId }, operators)) || operators[0];
  }

  /**
   * Get value of field which is in config mode based on keyName
   * @param {string} keyName
   * @returns {Object}
   */
  getValueByKey(keyName: string = '') {
    return this[keyName] || this.fieldDetails[keyName];
  }

  /**
   * Get value of field which is in config mode based on keyName
   * @param {string} aliasName
   */
  updateFilterConfig(aliasName: string) {
    const fieldDetails = this.fieldDetails;
    transaction(() => {
      // Only update filter value and filter operator when operator Id change
      if (this.selectedOperatorId !== fieldDetails.operatorId) {
        // reset filter value
        this.setFilterValue([]);
        fieldDetails.updateByKey('operatorId', this.selectedOperatorId);
      }
      fieldDetails.updateByKey('alias', aliasName);
    });
  }

  /**
   * Get group and operator by operator id
   * @param {string} operatorId
   */
  getGroupAndOperatorByOperatorId(operatorId: string) {
    let sGroup = this.getGroupByOperatorId(operatorId) || {};
    let sOperator = this.getOperatorById(operatorId) || {};
    const dataType = (this.fieldDetails && this.fieldDetails.dataType) || '';
    const defaultOperatorGroup = DEFAUFT_OPERATOR_GROUP[dataType.toLowerCase()];

    let sourceTypeByGroup;
    if (!sGroup.id) {
      sGroup = this.getGroupByKey(defaultOperatorGroup) || {};
      sOperator = sGroup.filterOperators && this.getOperatorByIndex(sGroup.id, dataType.toLowerCase());
    }

    sourceTypeByGroup = OPERATOR_VALUE_SOURCE[sGroup.key];
    sourceTypeByGroup = isObject(sourceTypeByGroup) ? sourceTypeByGroup[sOperator.key] : sourceTypeByGroup;

    return {
      group: sGroup,
      operator: sOperator,
      sourceTypeByGroup
    };
  }

  /**
   * Get FilterInfoDisplayName
   * @param {number} position
   * @param {Array<FilterDetailsConfig>} operatorSource
   */
  getFilterInfoDisplayName(position: number, operatorSource: Array<FilterDetailsConfig>) {
    const fieldDetails = this.fieldDetails || {};
    const filterInfo = this.filterInfo || {};
    const filterValue = this.filterValue || [];
    const fieldDataType = fieldDetails.dataType;

    // For case value is a field source (Operator group = comparision)
    const valueObject = find({ querySourceFieldId: filterValue[0] }, this.operatorSourceValue) || {};

    // For case value is BLANK, NOTBLANK, TRUE, FALSE
    const operator = find({ id: filterInfo.operatorId }, this.operators) || {};
    let value = includes(operator.name, OPERATOR_VALUE_LIST) ? operator.name : filterValue;

    const applyFormater = partial(reportPartUtils.applyFormater, [
      fieldDataType,
      get('dataFormat.format', fieldDetails),
      placeholder,
      null
    ]);
    const isUsingGuid = isArray(value) && value.length === 1 && String.isGuidFormat(value[0]);

    if (isUsingGuid) {
      const isTimePeriod = filterInfo.sourceType === 'inTimePeriod';
      const isFieldComparison = filterInfo.sourceType === 'fieldSource';
      if (isTimePeriod || isFieldComparison) {
        const source = flow(
          find(item => item.operatorSourceInfo.sourceType === filterInfo.sourceType),
          get('operatorSourceInfo.data')
        )(operatorSource);

        value = isFieldComparison
          ? flow(
            find({ querySourceFieldId: value[0] }),
            get('sourceFieldName')
          )(source)
          : flow(
            find({ id: value[0] }),
            get('name')
          )(source);
      } else {
        // For Field Compare
        value = valueObject.sourceFieldName;
      }
    } else if (isArray(value)) {
      value = flow(
        filter(negate(isEmpty)),
        map(applyFormater),
        join(', ')
      )(value);
    } else {
      value = applyFormater(value);
    }

    const mapToUserContextVariable = val =>
      val
        .replace('[Current User]', uiState.currentUser.userName)
        .replace('[Current Tenant]', uiState.currentUser.tenantDisplayID || '[Current Tenant]')
        .replace(/\[(User|Tenant|Role): (.+?)\]/g, '$2');
    !isEmpty(value) && (value = mapToUserContextVariable(value));

    return isEmpty(fieldDetails.alias) || isNil(filterInfo.operator)
      ? null
      : `${isFinite(position) ? position : fieldDetails.position} \
      ${fieldDetails.alias} \
      ${filterInfo.operator.label} \
      ${value}`;
  }

  /**
   * Set field details
   * @param {Object} fieldDetails
   */
  @action
  setFieldDetails(fieldDetails: any) {
    fieldDetails = fieldDetails || {};
    // update field details && set config mode to new field
    this.selectedOperatorId = fieldDetails.operatorId;
    this.fieldDetails = fieldDetails;
    this.resetOperatorSourceValue();
  }

  /**
   * Set operator source(different operator need to call different API)
   * @param {string} sourceType
   * @param {Object} operatorSourceValue
   */
  @action
  populateOperatorSourceValue(sourceType: string, operatorSourceValue: any) {
    const filterSource =
      sourceType === 'fieldSource'
        ? map(
          item =>
            !!item.sourceFieldVisible && {
              querySourceFieldId: isNil(item.relationshipId)
                ? item.querySourceFieldId
                : item.querySourceFieldId + '##' + item.relationshipId,
              sourceFieldName: item.sourceFieldName,
              sourceDataObjectName: item.sourceDataObjectName
            },
          operatorSourceValue
        )
        : operatorSourceValue;

    this.operatorSourceInfo.data = compact(filterSource);
    this.operatorSourceInfo.sourceType = sourceType;
    this.populatedData = true;
  }

  /**
   * Populate existing filter values
   * @param {Object} existingConfigField
   */
  @action
  populateExistingSourceValue(existingConfigField: any) {
    this.operatorSourceInfo = get('operatorSourceInfo', existingConfigField);
  }

  /**
   * Update operator source display
   * @param {boolean} isSearching
   */
  @action
  updateOperatorSourceDisplay({ result, isLastPage }, isSearching) {
    this.operatorSourceDisplay.isLastPage = isLastPage;
    isSearching && (this.operatorSourceDisplay.data = []);

    // We need to find default values in current data:
    // virtual node should have pure "fromIndex" for correct paging.
    let ignoredValues;
    if (this.operatorSourceDisplay.data.length)
      ignoredValues = filter(
        x => [DEFAUFT_VALUE.ALL, DEFAUFT_VALUE.BLANK, DEFAUFT_VALUE.NULL].indexOf(x.key) >= 0,
        this.operatorSourceDisplay.data);
    else
      ignoredValues = filter(
        x => [DEFAUFT_VALUE.ALL, DEFAUFT_VALUE.BLANK, DEFAUFT_VALUE.NULL].indexOf(x) >= 0,
        result);

    concatArray(this.operatorSourceDisplay.data, convertData(result, this.filterValue), 'key', !isLastPage,
      ignoredValues.length);

    return this.operatorSourceDisplay.data;

    function convertData(list, filterValue) {
      const isCheckAll = filterValue.length === 1 && filterValue[0] === DEFAUFT_VALUE.ALL;
      return list.map(value =>
        observable({
          key: value,
          value: value,
          label: value,
          checked: isCheckAll ? true : includes(value, filterValue)
        })
      );
    }
  }

  /**
   * Reset operator source display
   */
  @action
  resetOperatorSourceDisplay() {
    this.operatorSourceDisplay.data = [];
    this.operatorSourceDisplay.isLastPage = false;
  }

  /**
   * Reset operator source value
   */
  @action
  resetOperatorSourceValue() {
    this.operatorSourceInfo.data = [];
    this.operatorSourceInfo.sourceType = '';
  }

  /**
   * Set operator group id and reset operator Id (first item will be selectd)
   * @param {string} operatorGroupId
   */
  @action
  setOperatorGroupId(operatorGroupId: string) {
    // update operator - first item is selected by default
    const dataType = getOr('', 'dataType', this.fieldDetails);
    const operator = this.getOperatorByIndex(operatorGroupId, dataType.toLowerCase());
    this.setOperatorId(operator && operator.id);
  }

  /**
   * Set operator Id and reset filter value (empty string) for subscription.
   * @param {Object} subData
   * @param {string} filterFieldId
   * @param {boolean} isDashboardDetail
   */
  @action
  setOperatorIdSubscription(subData: any, filterFieldId: string, isDashboardDetail: boolean, { selectedOperatorId }) {
    this.selectedOperatorId = selectedOperatorId;
    this.fieldDetails && this.fieldDetails.updateByKey('operatorId', selectedOperatorId);

    if (isDashboardDetail) {
      return this.setCommonOperatorIdSubscription(subData, filterFieldId, selectedOperatorId);
    }

    const filter = find(s => isEqual(s.filterFieldId, filterFieldId), subData.subscriptionFilterFields);

    if (isNil(filter)) {
      subData.subscriptionFilterFields.push({
        filterFieldId,
        operatorId: selectedOperatorId,
        state: INSERT
      });
    } else {
      const i = indexOf(filter, subData.subscriptionFilterFields);
      subData.subscriptionFilterFields[i].operatorId = selectedOperatorId;
      subData.subscriptionFilterFields[i].state !== INSERT && (subData.subscriptionFilterFields[i].state = UPDATE);
    }

    // reset filter value
    this.setFilterValue([]);
  }

  /**
   * Set operator Id and reset filter value (empty string) for subscription.
   * @param {string} commonFilterFieldId
   * @param {string} operatorId
   */
  @action
  setCommonOperatorIdSubscription({ subscriptionCommonFilterFields }, commonFilterFieldId: string, operatorId: string) {
    this.selectedOperatorId = operatorId;
    this.fieldDetails && this.fieldDetails.updateByKey('operatorId', operatorId);

    const filter = find(s => isEqual(s.commonFilterFieldId, commonFilterFieldId), subscriptionCommonFilterFields);

    if (isNil(filter)) {
      subscriptionCommonFilterFields.push({
        commonFilterFieldId,
        operatorId,
        state: INSERT
      });
    } else {
      const i = indexOf(filter, subscriptionCommonFilterFields);
      subscriptionCommonFilterFields[i].operatorId = operatorId;
      subscriptionCommonFilterFields[i].state !== INSERT && (subscriptionCommonFilterFields[i].state = UPDATE);
    }

    // reset filter value
    this.setFilterValue([]);
  }

  /**
   * Set operator Id and reset filter value (empty string)
   * @param {string} operatorId
   * @param {boolean} resetFilterValue
   */
  @action
  setOperatorId(operatorId: string, resetFilterValue: boolean = true) {
    this.selectedOperatorId = operatorId;
    this.fieldDetails && this.fieldDetails.updateByKey('operatorId', operatorId);
    const operatorName = get('operator.name', this.filterInfo);
    this.fieldDetails.updateByKey('operatorName', operatorName);
    // reset filter value
    resetFilterValue && this.setFilterValue([]);
  }
}



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.FilterDetailsConfig.js